import React, { useEffect, useRef, useState } from 'react';
import {
  EnvelopeIcon,
  LockClosedIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import LogoBlack from '../../assets/logo-black.png';
import LogoWhite from '../../assets/white-logo-brand.svg';
import userApi from '../../services/userApi';
import {
  setIsAuthenticated,
  setRole,
  setRolePermissions,
} from '../../store/slices/authSlice';
import { setGlobalCompany } from '../../store/slices/companySlice';
import { useDispatch, useSelector } from 'react-redux';
import tokenService from '../../utils/tokenService';
import Notification from '../../components/notification/notification';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { TWInput, TWButton } from '../../components/ui';
import Turnstile from '../../components/turnstile/turnstile';

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const auth = useSelector((state) => state.authReducer.isAuthenticated);
  const dispatch = useDispatch();
  const [state, setState] = useState({
    email: '',
    password: '',
    loading: false,
    remember: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setState((prev) => ({ ...prev, email: savedEmail, remember: true }));
    } else {
      setState((prev) => ({ ...prev, remember: false }));
    }
  }, []);

  const [errors, setErrors] = useState({
    email: false,
    password: false,
    showNotf: false,
    message: '',
  });

  const rolePermissions = (permissions) => {
    const rolepermissions = [];
    permissions.forEach((permission) => {
      rolepermissions.push(permission.permission.code);
    });
    return rolepermissions;
  };

  const onClickLogin = async () => {
    if (state.email === '' || state.password === '') {
      setErrors((prev) => ({
        ...prev,
        email: state.email === '',
        password: state.password === '',
      }));
      return;
    }
    setErrors((prev) => ({ ...prev, email: false, password: false }));
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const res = await userApi.login(
        {
          email: state.email,
          password: state.password,
        },
        turnstileToken
      );
      if (res.data) {
        const roles = res.data.data.rolecodes;
        const perms = rolePermissions(
          res.data.data.permissions[0].rolepermissions,
        );
        tokenService.removeComp();
        tokenService.updateLocalAccessToken(res.data.data.token);
        tokenService.setRole(roles);
        tokenService.setRolePermissions(perms);
        if (res.data.data.companyid) {
          dispatch(setGlobalCompany({ id: res.data.data.companyid }));
        }
        if (state.remember) {
          localStorage.setItem('rememberedEmail', state.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        dispatch(setIsAuthenticated());
        dispatch(setRole(roles));
        dispatch(setRolePermissions(perms));
        navigate('/dashboard');
      }
    } catch (error) {
      turnstileRef.current?.reset();
      setErrors((prev) => ({
        ...prev,
        showNotf: true,
        message: error.response.data,
      }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') onClickLogin();
  };

  if (auth) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-white px-4 py-10 dark:bg-slate-950 sm:px-6">
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <Link
          to="https://thisway.co"
          className="mb-7 flex items-center justify-center"
        >
          <img
            src={LogoBlack}
            alt="thisway"
            className="h-9 w-auto dark:hidden sm:h-10"
          />
          <img
            src={LogoWhite}
            alt="thisway"
            className="hidden h-9 w-auto dark:block sm:h-10"
          />
        </Link>

        {/* Card */}
        <div className="rounded-2xl bg-white p-7 shadow-lg shadow-slate-900/[0.04] ring-1 ring-slate-200 dark:bg-slate-900 dark:shadow-black/40 dark:ring-slate-800 sm:p-10">
          {/* Greeting */}
          <div className="mb-7">
            <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white sm:text-4xl">
              Welcome back
            </h1>
            <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
              Sign in to your dashboard.
            </p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <TWInput
              label="Email"
              labelIcon={EnvelopeIcon}
              type="email"
              autoComplete="email"
              size="lg"
              required
              placeholder="you@restaurant.com"
              value={state.email}
              onChange={(e) =>
                setState((prev) => ({ ...prev, email: e.target.value }))
              }
              onKeyDown={onKeyDown}
              error={errors.email ? 'Enter your email' : undefined}
            />

            <div className="relative">
              <TWInput
                label="Password"
                labelIcon={LockClosedIcon}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                size="lg"
                required
                placeholder="Your password"
                value={state.password}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, password: e.target.value }))
                }
                onKeyDown={onKeyDown}
                error={errors.password ? 'Enter your password' : undefined}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-[2.1rem] flex h-6 w-6 items-center justify-center text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-200"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={state.remember}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      remember: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 dark:border-slate-600 dark:bg-slate-900"
                />
                Remember me
              </label>
              <Link
                to="forgot-password"
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
              >
                Forgot password?
              </Link>
            </div>

            <Turnstile ref={turnstileRef} onToken={setTurnstileToken} />

            <TWButton
              variant="primary"
              block
              size="lg"
              iconRight={ArrowRightIcon}
              label="Sign in"
              loading={state.loading}
              disabled={state.loading}
              onClick={onClickLogin}
            />
          </div>
        </div>

        {/* Sign-up CTA */}
        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          New to thisway?{' '}
          <Link
            to="/register"
            className="font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
          >
            Create an account
          </Link>
        </p>
      </div>

      <Notification
        display={errors.showNotf}
        type="error"
        message={errors.message}
        onClose={() => setErrors((prev) => ({ ...prev, showNotf: false }))}
      />
    </div>
  );
};

export default Login;
